import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Flame, CheckCircle2, AlertTriangle, Circle, Bell, BellPlus, X, MessageCircle, Timer, ChevronDown, Zap, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, todayStr, daysUntil, urgencyColor } from "../lib/api";
import { TutorAvatar } from "../components/TutorAvatar";
import { useAuth } from "../context/AuthContext";
import { AssignmentSpark } from "../components/AssignmentSpark";
import { StreakCelebration, checkStreakMilestone } from "../components/StreakCelebration";
import { MorningGreeting } from "../components/MorningGreeting";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [streak, setStreak] = useState({ streak: 0, checked_today: false });
  const [focusStats, setFocusStats] = useState({ sprints: 0, minutes: 0 });
  const [bests, setBests] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [remOpen, setRemOpen] = useState(false);
  const [remForm, setRemForm] = useState({ title: "", date: "" });
  const [expandedId, setExpandedId] = useState(null);
  const [milestone, setMilestone] = useState(null);

  const load = useCallback(async () => {
    const [a, s, r, f, b] = await Promise.all([
      api.get("/assignments"),
      api.get("/streak", { params: { today: todayStr() } }),
      api.get("/reminders", { params: { today: todayStr() } }),
      api.get("/focus/stats", { params: { today: todayStr() } }),
      api.get("/personal-bests", { params: { today: todayStr() } }),
    ]);
    setAssignments(a.data.filter((x) => !x.completed));
    setStreak(s.data);
    setReminders(r.data.active);
    setFocusStats(f.data);
    setBests(b.data);
    if (s.data.checked_today) {
      const m = checkStreakMilestone(s.data.streak);
      if (m) setMilestone(m);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    reminders.forEach((r) => {
      const k = `notified:${r.key}:${todayStr()}`;
      if (!localStorage.getItem(k)) {
        try {
          new Notification("Mia reminder", { body: `${r.title} — ${r.detail}` });
        } catch (e) {
          console.error("Browser notification failed", e);
        }
        localStorage.setItem(k, "1");
      }
    });
  }, [reminders]);

  const dismissReminder = async (key) => {
    await api.post("/reminders/dismiss", { key });
    setReminders((r) => r.filter((x) => x.key !== key));
  };

  const addReminder = async () => {
    if (!remForm.title || !remForm.date) {
      toast.error("Give the reminder a name and a date!");
      return;
    }
    await api.post("/reminders", remForm);
    toast.success("Reminder set!");
    setRemOpen(false);
    setRemForm({ title: "", date: "" });
    load();
  };

  const checkin = async () => {
    await api.post("/checkin", { date: todayStr() });
    const { data } = await api.get("/streak", { params: { today: todayStr() } });
    setStreak(data);
    const m = checkStreakMilestone(data.streak);
    if (m) setMilestone(m);
    else toast.success("Checked in! Mia sees you showing up 💪");
    load();
  };

  const toggleChunk = async (aid, idx, done) => {
    await api.patch(`/assignments/${aid}/chunks/${idx}`, { done: !done });
    if (!done) toast.success("Chunk done! That's a dopamine hit earned.");
    load();
  };

  const today = todayStr();
  const todaysChunks = assignments.flatMap((a) =>
    (a.chunks || []).map((c, i) => ({ ...c, aid: a.id, idx: i, atitle: a.title })).filter((c) => c.date <= today && !c.done)
  );
  const upcoming = assignments.slice(0, 4);
  const nextExam = assignments
    .filter((a) => ["exam", "test"].includes(a.type) && daysUntil(a.due_date) >= 0)
    .sort((a, b) => a.due_date.localeCompare(b.due_date))[0];
  const examDays = nextExam ? daysUntil(nextExam.due_date) : null;

  return (
    <div className="max-w-lg mx-auto px-5 pt-8 pb-32 space-y-6">
      {milestone && (
        <StreakCelebration milestone={milestone} childName={user?.child?.name} onClose={() => setMilestone(null)} />
      )}
      <div className="flex items-center gap-4">
        <TutorAvatar speaking={false} size="w-16 h-16" />
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-3xl font-semibold">Hey, {user?.child?.name || "superstar"}!</h1>
          <p className="text-gray-600 text-sm">Mia's got your back today.</p>
        </div>
        <Link
          to="/chat"
          data-testid="chat-icon-button"
          className="shrink-0 p-3 bg-[#4D96FF] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0px_rgba(0,0,0,1)] transition-all"
          title="Chat with Mia"
        >
          <MessageCircle size={22} strokeWidth={2.5} />
        </Link>
      </div>

      <MorningGreeting />

      {reminders.length > 0 && (
        <section className="space-y-2" data-testid="reminders-section">
          {reminders.slice(0, 5).map((r) => (
            <div
              key={r.key}
              data-testid={`reminder-banner-${r.key}`}
              className={`flex items-center gap-3 p-3.5 border-2 border-black rounded-xl shadow-[4px_4px_0px_rgba(0,0,0,1)] ${
                r.days_left <= 2 ? "bg-[#FF6B6B] text-white" : r.days_left <= 7 ? "bg-[#FFE066]" : r.days_left <= 14 ? "bg-[#F2C6DE]" : "bg-[#51CF66]"
              }`}
            >
              <Bell size={18} className="shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm leading-tight">{r.title}</p>
                <p className="text-xs opacity-80">{r.detail}</p>
              </div>
              <button
                onClick={() => dismissReminder(r.key)}
                data-testid={`dismiss-reminder-${r.key}`}
                className="p-1.5 rounded-full border-2 border-black bg-white text-black shadow-[2px_2px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all"
              >
                <X size={14} strokeWidth={3} />
              </button>
            </div>
          ))}
          {reminders.length > 5 && (
            <p className="text-xs text-gray-500 font-bold pl-1" data-testid="more-reminders-count">+{reminders.length - 5} more coming up — check the calendar</p>
          )}
        </section>
      )}

      <button
        onClick={() => setRemOpen(true)}
        data-testid="add-reminder-button"
        className="w-full flex items-center justify-center gap-2 bg-white border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0px_rgba(0,0,0,1)] transition-all font-bold px-6 py-2.5 text-sm"
      >
        <BellPlus size={18} /> Add a reminder
      </button>

      <Dialog open={remOpen} onOpenChange={setRemOpen}>
        <DialogContent className="border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">New reminder</DialogTitle>
            <DialogDescription>It'll pop up on this screen (and as a notification) from that day.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input
              className="w-full bg-white border-2 border-black rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[#FFE066]/50 text-base"
              placeholder="Remind me to..."
              value={remForm.title}
              onChange={(e) => setRemForm({ ...remForm, title: e.target.value })}
              data-testid="reminder-title-input"
            />
            <input
              type="date"
              className="w-full bg-white border-2 border-black rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[#FFE066]/50 text-base"
              value={remForm.date}
              onChange={(e) => setRemForm({ ...remForm, date: e.target.value })}
              data-testid="reminder-date-input"
            />
            <button
              onClick={addReminder}
              data-testid="reminder-save-button"
              className="w-full bg-[#FFE066] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold py-3"
            >
              Set reminder
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {nextExam && (
        <div
          className={`border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-5 ${
            examDays <= 3 ? "bg-[#FF6B6B] text-white" : examDays <= 7 ? "bg-[#FFE066]" : "bg-[#4D96FF] text-white"
          }`}
          data-testid="exam-countdown-card"
        >
          <div className="flex items-center gap-4">
            <div className="text-center shrink-0">
              <p className="font-heading text-6xl font-bold leading-none" data-testid="exam-countdown-days">
                {examDays}
              </p>
              <p className="text-[11px] font-bold tracking-[0.15em] uppercase mt-1">{examDays === 1 ? "day" : "days"} to go</p>
            </div>
            <div className="flex-1 min-w-0 border-l-2 border-black/30 pl-4">
              <p className="text-[11px] font-bold tracking-[0.15em] uppercase opacity-80">Next {nextExam.type}</p>
              <p className="font-heading text-xl font-semibold leading-tight">{nextExam.title}</p>
              <p className="text-sm opacity-90">{nextExam.subject} · {nextExam.due_date.slice(0, 10)}</p>
            </div>
          </div>
          {examDays <= 14 && (
            <button
              onClick={() => navigate("/study", { state: { prefill: { title: nextExam.title, subject: nextExam.subject } } })}
              data-testid="build-study-set-button"
              className="mt-4 w-full bg-white text-black border-2 border-black rounded-full shadow-[3px_3px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all font-bold py-2.5 text-sm flex items-center justify-center gap-2"
            >
              <BookOpen size={16} /> {examDays <= 7 ? "Crunch time — build a study set with Mia" : "Start early — build a study set with Mia"}
            </button>
          )}
        </div>
      )}

      <div className="bg-[#FFE066] border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6" data-testid="streak-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Flame size={32} strokeWidth={2.5} className="text-[#FF6B6B]" />
            <div>
              <p className="font-heading text-2xl font-semibold" data-testid="streak-count">{streak.streak} day streak</p>
              <p className="text-sm text-gray-700">Show up daily. Tiny wins add up.</p>
            </div>
          </div>
          {streak.checked_today ? (
            <CheckCircle2 size={32} className="text-[#51CF66]" data-testid="checked-in-icon" />
          ) : (
            <button
              onClick={checkin}
              data-testid="checkin-button"
              className="bg-white text-black border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0px_rgba(0,0,0,1)] transition-all font-bold px-5 py-2"
            >
              Check in
            </button>
          )}
        </div>
      </div>

      <section className="space-y-3">
        <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
          <AlertTriangle size={20} className="text-[#FF6B6B]" /> Coming up
        </h2>
        {upcoming.length === 0 && (
          <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6 text-gray-600" data-testid="no-assignments-msg">
            Nothing in the calendar yet. <Link to="/upload" className="font-bold underline">Upload your assessment schedule</Link> or add tasks in the calendar.
          </div>
        )}
        {upcoming.map((a) => {
          const d = daysUntil(a.due_date);
          const open = expandedId === a.id;
          return (
            <div key={a.id} className="bg-white border-2 border-black rounded-xl shadow-[4px_4px_0px_rgba(0,0,0,1)]">
              <button
                onClick={() => setExpandedId(open ? null : a.id)}
                data-testid={`home-assignment-${a.id}`}
                className="w-full flex items-center justify-between gap-2 p-4 text-left"
              >
                <div className="min-w-0">
                  <p className="font-bold">{a.title}</p>
                  <p className="text-sm text-gray-600">{a.subject} · {a.type}</p>
                </div>
                <span className={`shrink-0 inline-flex items-center px-3 py-1 rounded-full border-2 border-black text-xs font-bold shadow-[2px_2px_0px_rgba(0,0,0,1)] ${urgencyColor(d)}`}>
                  {d < 0 ? "overdue!" : d === 0 ? "today!" : `${d} day${d === 1 ? "" : "s"}`}
                </span>
                <ChevronDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && <div className="px-4 pb-4"><AssignmentSpark assignment={a} /></div>}
            </div>
          );
        })}
      </section>

      <section className="space-y-3">
        <h2 className="font-heading text-xl font-semibold">Today's chunks</h2>
        {todaysChunks.length === 0 ? (
          <p className="text-gray-600 text-sm">No chunks due today. Break an assignment down in the calendar to get your daily bites.</p>
        ) : (
          todaysChunks.map((c) => (
            <button
              key={`${c.aid}-${c.idx}`}
              onClick={() => toggleChunk(c.aid, c.idx, c.done)}
              data-testid={`home-chunk-${c.aid}-${c.idx}`}
              className="w-full flex items-center gap-3 p-4 bg-white border-2 border-black rounded-xl shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all text-left"
            >
              <Circle size={22} className="shrink-0 text-gray-400" />
              <div>
                <p className="font-bold text-sm">{c.title}</p>
                <p className="text-xs text-gray-500">{c.atitle}</p>
              </div>
            </button>
          ))
        )}
      </section>

      {focusStats.sprints > 0 && (
        <div className="flex items-center gap-3 bg-white border-2 border-black rounded-xl shadow-[4px_4px_0px_rgba(0,0,0,1)] p-4" data-testid="sprint-stats-card">
          <Zap size={22} className="text-[#4D96FF] shrink-0" />
          <p className="text-sm font-bold">
            {focusStats.sprints} sprint{focusStats.sprints === 1 ? "" : "s"} this week — {focusStats.minutes} focused minute{focusStats.minutes === 1 ? "" : "s"}. Keep stacking!
          </p>
        </div>
      )}

      {bests && (bests.best_streak > 1 || bests.total_sprints > 0) && (
        <div className="bg-[#0A0A0A] text-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-5" data-testid="personal-bests-card">
          <p className="text-[11px] font-bold tracking-[0.15em] uppercase text-gray-400 mb-3">Race yourself — personal bests</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-heading text-3xl font-bold">{streak.streak}<span className="text-base text-gray-400"> / {bests.best_streak}</span></p>
              <p className="text-xs font-bold text-gray-300 mt-0.5">day streak vs record</p>
              <div className="mt-2 h-2 bg-white/15 rounded-full border border-white/30">
                <div className="h-full bg-[#FF6B6B] rounded-full transition-all" style={{ width: `${Math.min(100, bests.best_streak ? (streak.streak / bests.best_streak) * 100 : 0)}%` }} />
              </div>
            </div>
            <div>
              <p className="font-heading text-3xl font-bold">{bests.this_week_minutes}<span className="text-base text-gray-400"> / {bests.best_week_minutes}</span></p>
              <p className="text-xs font-bold text-gray-300 mt-0.5">focus min vs best week</p>
              <div className="mt-2 h-2 bg-white/15 rounded-full border border-white/30">
                <div className="h-full bg-[#51CF66] rounded-full transition-all" style={{ width: `${Math.min(100, bests.best_week_minutes ? (bests.this_week_minutes / bests.best_week_minutes) * 100 : 0)}%` }} />
              </div>
            </div>
          </div>
          {(streak.streak >= bests.best_streak || bests.this_week_minutes >= bests.best_week_minutes) && bests.total_sprints > 0 && (
            <p className="mt-3 text-xs font-bold text-[#FFE066]">You're ON a record pace right now — don't let it slip!</p>
          )}
        </div>
      )}

      <Link
        to="/focus"
        data-testid="start-focus-button"
        className="bg-[#4D96FF] text-black border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0px_rgba(0,0,0,1)] transition-all font-bold px-6 py-3 flex items-center justify-center gap-2 w-full"
      >
        <Timer size={18} /> Focus sprint with Mia
      </Link>
    </div>
  );
}
